import axios, { AxiosError } from "axios";
import { drw_endpoint_url } from "../api/api";
import { getStore } from "../getStore";
import { getAppId, getPartnerCode } from "../selector/PartnerDetailsSelectors";

/**
 * Reads the Amazon Connect contactId from the URL query string, the same way
 * the legacy Jacada bundle read `conversationId` from `window.location.search`.
 */
export const getContactIdFromUrl = (): string | null => {
  return new URLSearchParams(window.location.search).get("contactId");
};

/**
 * Parallel to Jacada/Uniphore's `lib.createIntent`: POSTs an intent so a live
 * agent/bot session can react to it. Routed through wle-proxy's
 * `/api/voice/.../converse` endpoint (same host/pattern as the existing NLP
 * `/api/omniapi` calls) instead of the previous direct, unauthenticated POST
 * to AWS Connect's voiceconnector.awsconnect.io domain - wle-proxy
 * authenticates to drw-proxy's voice connector on our behalf.
 *
 * Body shape matches wle-proxy's expected contract:
 * `{ arsMessageId, arsAction? }`, where
 * `arsMessageId` carries the intent text and `arsAction` is only supplied
 * (e.g. `"disconnect"`) on the final submission of a Rescue Tracker session.
 */
const getConverseUrl = (): string | null => {
  const contactId = getContactIdFromUrl();
  if (!contactId) return null;

  const state = getStore().store?.getState();
  const appId = getAppId(state);
  const partnerCode = getPartnerCode(state);
  if (!appId || !partnerCode) return null;

  return `${drw_endpoint_url}/api/voice/${appId}/partner/${partnerCode}/contact/${contactId}/converse`;
};

/**
 * Whether an intent can actually be POSTed right now (contactId in the URL and
 * appId/partnerCode loaded into the store). Lets callers defer an intent
 * rather than losing it when context isn't available yet.
 */
export const isAwsConnectIntentReady = (): boolean => getConverseUrl() !== null;

export const createAwsConnectIntent = (intentText: string, arsAction?: string): Promise<unknown> => {
  const url = getConverseUrl();
  if (!url) {
    return Promise.resolve();
  }

  const payload: { arsMessageId: string; arsAction?: string } = { arsMessageId: intentText };
  if (arsAction) {
    payload.arsAction = arsAction;
  }

  return axios.post(url, payload, { headers: { "Content-Type": "application/json" } });
};

/**
 * True only when we know the converse POST never reached wle-proxy: axios
 * raised a network error and no HTTP response was received (offline, DNS
 * failure, connection refused/reset). Any HTTP status, timeout or cancellation
 * means the request may have been processed, so it is NOT safe to resend.
 */
export const isRequestNotDelivered = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;
  return axiosError?.isAxiosError === true && !axiosError.response && axiosError.code === "ERR_NETWORK";
};
